import React, { useMemo } from "react";

import { User } from "shared/assets/icons";

import { IEditUserProfile } from "Auth/containers";
import {
  FormBuilder,
  Title,
  Button,
  ButtonContainer,
  FancyButtonLink,
  Card,
} from "shared/components";
import { Spacer } from "shared/layout";
import { ITheme, IStatus, IAPIState, IAPIError } from "shared/types";
import { copy } from "shared/assets";

import { getFormFields, getDefaultValues, buttons } from "./formData";
import { IUser } from "Auth/types";

const { auth } = copy;

interface EditUserProfileProps {
  currentUser: IUser | null;
  onSubmit: (data: IEditUserProfile) => void;
  handleLogout: () => void;
  showChangeEmail: () => void;
  showChangePassword: () => void;
  showDeleteUser: () => void;
  status?: IAPIState;
  error?: string;
  apiError?: IAPIError | null;
  hasError?: boolean;
  showOtherInfo?: boolean;
}

export const EditUserProfile: React.FC<EditUserProfileProps> = ({
  currentUser,
  onSubmit,
  handleLogout,
  showChangeEmail,
  showChangePassword,
  showDeleteUser,
  status,
  hasError,
  apiError,
  showOtherInfo = false,
}) => {
  const { firstname, lastname, email } = currentUser?.attributes || {};

  const defaultValues = useMemo(() => getDefaultValues(currentUser), [currentUser]);

  const fields = useMemo(
    () =>
      getFormFields(
        () => showChangeEmail(),
        () => showChangePassword(),
        showOtherInfo
      ),
    [showChangeEmail, showChangePassword, showOtherInfo]
  );

  return (
    <Card title={auth.title} icon={User}>
      <Spacer size="m">
        <Spacer spaceDirection="horizontal" align="center" size="s">
          <Title title={`${firstname} ${lastname}`} asComponent="h2" />
          <Button onClick={handleLogout} size="s" theme={ITheme.secondary}>
            {copy.auth.logout}
          </Button>
        </Spacer>
        <FormBuilder
          fields={fields}
          buttons={buttons}
          defaultValues={defaultValues}
          forceUpdateValues={{
            "loginInfo.email": email,
          }}
          onSubmit={onSubmit}
          submitMode="isDirty"
          isSubmitting={status === IAPIState.pending}
          apiErrorMessage={hasError}
          apiError={apiError}
        />
        <ButtonContainer>
          <FancyButtonLink
            status={IStatus.error}
            onClick={() => showDeleteUser()}
            data-testid="user_delete_button"
          >
            {copy.auth.delete}
          </FancyButtonLink>
        </ButtonContainer>
      </Spacer>
    </Card>
  );
};
